import { Component, Inject, OnInit, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageTitleService } from '@app/shared/services/page-title.service';
import { Transition } from '@uirouter/angular';
import { switchMap, first } from 'rxjs/operators';

import browserHelper from '@utils/browser-helper';
import dayjs from 'dayjs';

import { CompanyService } from '@app/shared/services/company.service';
import { LegalTermsResource } from '@app/shared/modules/resources/legal-terms.resource';
import { UserManager } from '@app/shared/services/user-manager.service';
import { ScrollToIdService } from '@app/shared/services/scroll-to-id.service';

import { TabsModule } from '@app/modules/ui/tabs/tabs.module';
import { TableOfContentsModule } from '@app/modules/ui/table-of-contents/table-of-contents.module';
import { IconsModule } from '@app/shared/modules/icons/icon.module';
import { LogoModule } from '@app/shared/modules/logo/logo.module';
import { TranslateModule } from '@app/shared/modules/translate/translate.module';
import { UIRouterModule } from '@uirouter/angular';

@Component({
  selector: 'app-legal-terms',
  styleUrls: ['./legal-terms-new.component.scss'],
  templateUrl: './legal-terms-new.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TabsModule, TableOfContentsModule, IconsModule, LogoModule, TranslateModule, UIRouterModule]
})
export class LegalTermsNewComponent implements OnInit {
  isLoaded = signal(false);
  isPublic = computed(() => this.APP === 'public');
  documentContents = signal<string | undefined>(undefined);
  lastUpdate = signal('');
  document = signal<string>(this.transition.params().document as string);

  constructor(
    @Inject('APP') public APP: string,
    private userManager: UserManager,
    private companyManager: CompanyService,
    private pageTitle: PageTitleService,
    private transition: Transition,
    private legalTermsResource: LegalTermsResource,
    private scrollToIdService: ScrollToIdService
  ) {}

  ngOnInit() {
    this.loadLegalTerms();

    if (this.isPublic()) {
      this.scrollToIdService.scrollToId();
    }
  }

  private loadLegalTerms() {
    this.legalTermsResource
      .getLocales()
      .pipe(
        switchMap(locales => {
          const preferredLocale = this.getPreferredLocale(locales);
          return this.legalTermsResource.get(preferredLocale);
        }),
        first()
      )
      .subscribe(terms => this.handleLegalTermsResponse(terms));
  }

  private getPreferredLocale(locales: { available: string[]; primary: string }) {
    const possibleLocales = [
      this.userManager.data.locale, // 1 User preference
      browserHelper.preferredLanguage, // 2 browser
      locales.primary // value from api
    ].filter(Boolean);

    return possibleLocales.map(pl => locales.available.find(al => this.compareLocales(pl, al))).find(Boolean);
  }

  private handleLegalTermsResponse(terms: any) {
    this.lastUpdate.set(dayjs(terms.updatedAt).format('YYYY-MM-DD'));

    const camelCasedDocument = this.document().replace(/-([a-z])/g, g => g[1].toUpperCase());
    this.documentContents.set(terms[camelCasedDocument]);

    this.pageTitle.setTitle(`page-titles.legal-terms.${this.document()}`);

    this.isLoaded.set(true);
  }

  get companyName() {
    return this.companyManager.data.name;
  }

  compareLocales(a: string, b: string) {
    const shortLocaleA = a.slice(0, 2).toLowerCase();
    const shortLocaleB = b.slice(0, 2).toLowerCase();

    return shortLocaleA === shortLocaleB;
  }
}
